/**
 * ApiDrivenReportPullForCustomBiDashboardsPage — developer-facing surface for the
 * `/api/v1/api-driven-report-pull-for-custom-bi-dashboards` BE (FS-334).
 *
 * Sections (top → bottom):
 *   1. Header — "API-driven report pull" title + one-line overview.
 *   2. Range + form — ISO date picker (start / end) with inline validation,
 *      report type + page size inputs, pull_id nonce.
 *   3. Curl example — live, copy-ready curl for POST /pull (auto-updates on
 *      form change). Copy button, deterministic snippet via lib.buildCurlSnippet.
 *   4. Warehouse exports — two side-by-side panels (Snowflake + BigQuery) with
 *      loader commands and copy buttons.
 *   5. Schema preview — labeled request/response shape so BI engineers can
 *      eyeball the payload before running curl.
 *   6. Recent pulls — small read-only list of existing pulls for this tenant.
 *
 * Brand (per CLAUDE.md):
 *   - green-600 / green-700 / green-50 accent (NO emerald/blue/orange/purple).
 *   - white surface, zinc-200 borders, Inter via system font stack.
 *   - No dark mode, no emoji, no competitor names. Snowflake/BigQuery kept
 *     (data-warehouse names, not affiliate-tracker competitors).
 */
import { useMemo, useState } from "react";
import { useApiDrivenReportPullForCustomBiDashboardsList } from "./hooks";
import {
	RESPONSE_SCHEMA,
	buildCurlSnippet,
	buildWarehouseSnippet,
	validateDateRange,
} from "./lib";

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-green-700 hover:border-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50";
const INPUT =
	"w-full rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 transition-colors focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const LABEL = "text-xs font-medium text-zinc-600";
const CODE_BLOCK =
	"overflow-x-auto rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 font-mono text-xs leading-relaxed text-zinc-800";
const PILL_ACTIVE = "border-green-600 bg-green-50 text-green-700";
const PILL_IDLE = "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50";

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface FormState {
	pullId: string;
	reportType: string;
	start: string;
	end: string;
	pageSize: number;
	tenantId: string;
	baseUrl: string;
}

const today = new Date().toISOString().slice(0, 10);
const sevenDaysAgo = new Date(Date.now() - 6 * 86_400_000).toISOString().slice(0, 10);

const DEFAULTS: FormState = {
	pullId: "demo-pull-01",
	reportType: "performance_daily",
	start: sevenDaysAgo,
	end: today,
	pageSize: 100,
	tenantId: "tenant_demo",
	baseUrl: typeof window !== "undefined" ? window.location.origin : "https://api.example.com",
};

// ---------------------------------------------------------------------------
// Copy helper (clipboard with safe fallback)
// ---------------------------------------------------------------------------

async function copyText(text: string): Promise<boolean> {
	try {
		if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch {
		// fallthrough to failure
	}
	return false;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ApiDrivenReportPullForCustomBiDashboardsPage() {
	const [form, setForm] = useState<FormState>(DEFAULTS);
	const [copied, setCopied] = useState<string | null>(null);
	const listQuery = useApiDrivenReportPullForCustomBiDashboardsList();

	const rangeCheck = useMemo(
		() => validateDateRange({ start: form.start, end: form.end }),
		[form.start, form.end],
	);

	const curlSnippet = useMemo(
		() =>
			buildCurlSnippet({
				baseUrl: form.baseUrl,
				tenantId: form.tenantId,
				pullId: form.pullId,
				reportType: form.reportType,
				start: form.start,
				end: form.end,
				pageSize: form.pageSize,
			}),
		[form],
	);

	const snowflakeSnippet = useMemo(
		() =>
			buildWarehouseSnippet("snowflake", {
				baseUrl: form.baseUrl,
				tenantId: form.tenantId,
				pullId: form.pullId,
				reportType: form.reportType,
				start: form.start,
				end: form.end,
				pageSize: form.pageSize,
			}),
		[form],
	);

	const bigQuerySnippet = useMemo(
		() =>
			buildWarehouseSnippet("bigquery", {
				baseUrl: form.baseUrl,
				tenantId: form.tenantId,
				pullId: form.pullId,
				reportType: form.reportType,
				start: form.start,
				end: form.end,
				pageSize: form.pageSize,
			}),
		[form],
	);

	const listData = listQuery.data as { data?: unknown[] } | undefined;
	const recent = Array.isArray(listData?.data) ? (listData!.data as Array<Record<string, unknown>>) : [];

	async function handleCopy(key: string, text: string) {
		const ok = await copyText(text);
		if (ok) {
			setCopied(key);
			window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
		}
	}

	const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
		setForm((p) => ({ ...p, [k]: v }));

	return (
		<main
			data-testid="api-driven-report-pull-for-custom-bi-dashboards-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-10 font-sans text-zinc-900"
		>
			{/* Header */}
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Developer API
				</p>
				<h1
					className="text-2xl font-semibold text-zinc-900"
					data-testid="api-driven-report-pull-for-custom-bi-dashboards-title"
				>
					API-driven report pull
				</h1>
				<p className="text-sm text-zinc-500">
					Pull report data on demand for custom BI dashboards. Idempotent by
					tenant + <code className="font-mono text-[11px]">pull_id</code>;
					paginate via cursor.
				</p>
			</header>

			{/* Range + form */}
			<section
				className={CARD}
				aria-label="Query parameters"
				data-testid="api-driven-report-pull-for-custom-bi-dashboards-form"
			>
				<h2 className="mb-3 text-sm font-semibold text-zinc-900">Parameters</h2>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
					<Field label="Start date">
						<input
							type="date"
							className={INPUT}
							value={form.start}
							onChange={(e) => update("start", e.currentTarget.value)}
							data-testid="api-driven-report-pull-for-custom-bi-dashboards-start"
						/>
					</Field>
					<Field label="End date">
						<input
							type="date"
							className={INPUT}
							value={form.end}
							onChange={(e) => update("end", e.currentTarget.value)}
							data-testid="api-driven-report-pull-for-custom-bi-dashboards-end"
						/>
					</Field>
					<Field label="Report type">
						<input
							type="text"
							className={INPUT}
							value={form.reportType}
							onChange={(e) => update("reportType", e.currentTarget.value)}
							data-testid="api-driven-report-pull-for-custom-bi-dashboards-report-type"
						/>
					</Field>
					<Field label="Page size">
						<input
							type="number"
							min={1}
							max={500}
							className={INPUT}
							value={form.pageSize}
							onChange={(e) => update("pageSize", Number(e.currentTarget.value) || 0)}
							data-testid="api-driven-report-pull-for-custom-bi-dashboards-page-size"
						/>
					</Field>
					<Field label="Pull ID (client nonce)">
						<input
							type="text"
							className={INPUT}
							value={form.pullId}
							onChange={(e) => update("pullId", e.currentTarget.value)}
							data-testid="api-driven-report-pull-for-custom-bi-dashboards-pull-id"
						/>
					</Field>
					<Field label="Tenant ID">
						<input
							type="text"
							className={INPUT}
							value={form.tenantId}
							onChange={(e) => update("tenantId", e.currentTarget.value)}
							data-testid="api-driven-report-pull-for-custom-bi-dashboards-tenant-id"
						/>
					</Field>
					<Field label="Base URL">
						<input
							type="text"
							className={INPUT}
							value={form.baseUrl}
							onChange={(e) => update("baseUrl", e.currentTarget.value)}
							data-testid="api-driven-report-pull-for-custom-bi-dashboards-base-url"
						/>
					</Field>
				</div>
				{!rangeCheck.ok ? (
					<p
						className="mt-3 inline-flex items-center gap-1 rounded-[6px] border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
						role="alert"
						data-testid="api-driven-report-pull-for-custom-bi-dashboards-range-error"
					>
						{rangeCheck.message}
					</p>
				) : (
					<p
						className="mt-3 inline-flex items-center gap-1 rounded-[6px] border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-700"
						data-testid="api-driven-report-pull-for-custom-bi-dashboards-range-ok"
					>
						Range valid — {rangeCheck.start} to {rangeCheck.end}.
					</p>
				)}
			</section>

			{/* Curl example */}
			<section className={CARD} aria-label="Curl example">
				<div className="mb-3 flex items-center justify-between">
					<h2 className="text-sm font-semibold text-zinc-900">Curl example</h2>
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={() => handleCopy("curl", curlSnippet)}
						data-testid="api-driven-report-pull-for-custom-bi-dashboards-copy-curl"
						aria-label="Copy curl snippet"
					>
						{copied === "curl" ? "Copied" : "Copy curl"}
					</button>
				</div>
				<pre
					className={CODE_BLOCK}
					data-testid="api-driven-report-pull-for-custom-bi-dashboards-curl"
				>
					{curlSnippet}
				</pre>
			</section>

			{/* Warehouse exports */}
			<section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<article className={CARD} aria-label="Snowflake export snippet">
					<div className="mb-3 flex items-center justify-between">
						<h3 className="text-sm font-semibold text-zinc-900">Snowflake</h3>
						<button
							type="button"
							className={BTN_GHOST}
							onClick={() => handleCopy("snowflake", snowflakeSnippet)}
							data-testid="api-driven-report-pull-for-custom-bi-dashboards-copy-snowflake"
							aria-label="Copy Snowflake snippet"
						>
							{copied === "snowflake" ? "Copied" : "Copy"}
						</button>
					</div>
					<pre
						className={CODE_BLOCK}
						data-testid="api-driven-report-pull-for-custom-bi-dashboards-snowflake"
					>
						{snowflakeSnippet}
					</pre>
				</article>
				<article className={CARD} aria-label="BigQuery export snippet">
					<div className="mb-3 flex items-center justify-between">
						<h3 className="text-sm font-semibold text-zinc-900">BigQuery</h3>
						<button
							type="button"
							className={BTN_GHOST}
							onClick={() => handleCopy("bigquery", bigQuerySnippet)}
							data-testid="api-driven-report-pull-for-custom-bi-dashboards-copy-bigquery"
							aria-label="Copy BigQuery snippet"
						>
							{copied === "bigquery" ? "Copied" : "Copy"}
						</button>
					</div>
					<pre
						className={CODE_BLOCK}
						data-testid="api-driven-report-pull-for-custom-bi-dashboards-bigquery"
					>
						{bigQuerySnippet}
					</pre>
				</article>
			</section>

			{/* Schema preview */}
			<section
				className={CARD}
				aria-label="Schema preview"
				data-testid="api-driven-report-pull-for-custom-bi-dashboards-schema"
			>
				<h2 className="mb-3 text-sm font-semibold text-zinc-900">Schema preview</h2>
				<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
					<SchemaBlock
						label="POST /pull"
						pill="Idempotent"
						body={RESPONSE_SCHEMA.POST_pull}
					/>
					<SchemaBlock
						label="GET /"
						pill="Cursor paged"
						body={RESPONSE_SCHEMA.GET_list}
					/>
				</div>
			</section>

			{/* Recent pulls */}
			<section
				className={CARD}
				aria-label="Recent pulls"
				data-testid="api-driven-report-pull-for-custom-bi-dashboards-recent"
			>
				<div className="mb-3 flex items-center justify-between">
					<h2 className="text-sm font-semibold text-zinc-900">Recent pulls</h2>
					<span className="text-xs text-zinc-500">
						{listQuery.isLoading ? "Loading" : `${recent.length} rows`}
					</span>
				</div>
				{listQuery.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="api-driven-report-pull-for-custom-bi-dashboards-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="text-sm text-red-600"
						role="alert"
						data-testid="api-driven-report-pull-for-custom-bi-dashboards-error"
					>
						{(listQuery.error as Error).message}
					</p>
				) : recent.length === 0 ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="api-driven-report-pull-for-custom-bi-dashboards-empty"
					>
						No pulls yet.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse text-sm"
							data-testid="api-driven-report-pull-for-custom-bi-dashboards-table"
						>
							<thead>
								<tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
									<th className="px-3 py-2">Pull ID</th>
									<th className="px-3 py-2">Report</th>
									<th className="px-3 py-2">Rows</th>
									<th className="px-3 py-2">Status</th>
								</tr>
							</thead>
							<tbody>
								{recent.map((r, idx) => {
									const id = String(r["id"] ?? idx);
									const status = String(r["status"] ?? "pending");
									const active = status === "delivered";
									return (
										<tr
											key={id}
											className="border-b border-zinc-100 last:border-b-0"
											data-testid={`api-driven-report-pull-for-custom-bi-dashboards-row-${id}`}
										>
											<td className="px-3 py-2 font-mono text-xs text-zinc-700">
												{String(r["pull_id"] ?? "—")}
											</td>
											<td className="px-3 py-2 text-zinc-900">
												{String(r["report_type"] ?? "—")}
											</td>
											<td className="px-3 py-2 tabular-nums text-zinc-900">
												{typeof r["row_count"] === "number"
													? (r["row_count"] as number).toLocaleString()
													: "0"}
											</td>
											<td className="px-3 py-2">
												<span
													className={
														"inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium " +
														(active ? PILL_ACTIVE : PILL_IDLE)
													}
												>
													{status}
												</span>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default ApiDrivenReportPullForCustomBiDashboardsPage;

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<label className="flex flex-col gap-1">
			<span className={LABEL}>{label}</span>
			{children}
		</label>
	);
}

function SchemaBlock({
	label,
	pill,
	body,
}: {
	label: string;
	pill: string;
	body: unknown;
}) {
	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center justify-between">
				<code className="font-mono text-xs font-semibold text-zinc-900">{label}</code>
				<span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${PILL_ACTIVE}`}>
					{pill}
				</span>
			</div>
			<pre className={CODE_BLOCK}>{JSON.stringify(body, null, 2)}</pre>
		</div>
	);
}
